import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaEye, FaTrash, FaEdit } from 'react-icons/fa';
import { Button, Table, Modal, Input, Space , Spin} from 'antd';
import '../../ViewTable/ViewEmployee.css';
import { useNavigate } from 'react-router-dom';
import { updateEmployeeRequest, fetchEmployeeRequest, deleteEmployeeRequest } from '../../../redux/actions/empActions.js';
 import EditEmployeeForm from "../../Forms/Employee/EditEmployee";
 import ViewEmployeeDetails from "../../Forms/Employee/ViewEmployeeDetails";
 import ConfirmationModal from '../../ConfirmationModel/ConfirmationModel.js';

const EmployeeTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux State
  const { data = [], loading} = useSelector(state => state.employee || {});
  const isMobile = window.innerWidth <= 768;

  // Local State
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredEmployees, setFilteredEmployees] = useState(data);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Fetch data on mount
  useEffect(() => {
    dispatch(fetchEmployeeRequest(4)); // Assuming 3 is the employee type id for managers
  }, [dispatch]);

  // Handle Search Query Filtering with Debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery === '') {
        setFilteredEmployees(data);
      } else {
        const filtered = data.filter(employee =>
          employee.empno.toString().includes(searchQuery) ||
          (`${employee.firstname} ${employee.lastname}`.toLowerCase().includes(searchQuery.toLowerCase()))
        );
        setFilteredEmployees(filtered);
      }
    }, 500); // Delay search by 500ms for performance optimization

    return () => clearTimeout(timer); 
  }, [searchQuery, data]);

  // Handle Add Employee Button
  const handleAddEmployee = () => {
    navigate('/employeeform');
  };

  // // Handle Delete Employee
  // const handleDelete = (employee) => {
  //   dispatch(deleteEmployeeRequest(employee));
  // };

  // Handle Edit Employee
  const handleEditClick = (employee) => {
    setSelectedEmployee(employee);
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  // Handle View Employee Details
  const handleViewDetails = (employee) => {
    setSelectedEmployee(employee);
    setIsEditMode(false);
    setIsModalOpen(true);
  };

  // Handle Save/Update Employee
  const handleEditEmployee = (updatedEmployee) => {
    dispatch(updateEmployeeRequest(updatedEmployee));
    setIsModalOpen(false);
  };

   // Open confirmation modal
   const openDeleteModal = (employee) => {
    setEmployeeToDelete(employee);
    setIsConfirmModalVisible(true);
  };

  // Handle confirm delete
  const handleConfirmDelete = () => {
    if (employeeToDelete) {
      dispatch(deleteEmployeeRequest(employeeToDelete));
      setIsConfirmModalVisible(false);
      setEmployeeToDelete(null);
    }
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setIsConfirmModalVisible(false);
    setEmployeeToDelete(null);
  };

  // Close Modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedEmployee(null);
    setIsEditMode(false);
  };

  // Table Columns
  const columns = [
    {
      title: 'Sr. No.',
      render: (_, __, index) => index + 1, // Render serial number starting from 1 
    },
    {
      title: 'Employee No',
      dataIndex: 'empno',
      sorter: (a, b) => a.sitename.localeCompare(b.sitename),
      width: 100,
    },
    {
      title: ' Name',
      sorter: (a, b) => a.sitename.localeCompare(b.sitename),
      render: (_, record) => (
        <span>
          {record.firstname} {record.middlename ? record.middlename : ''} {record.lastname}
        </span>
      ),
    },
    {
      title: 'Site Name',
      dataIndex: 'sitename', 
      sorter: (a, b) => a.sitename.localeCompare(b.sitename),
    },
    {
      title: 'Address',
      render: (_, record) => ( 
        <div className="address-content">
        {`${record.address}, ${record.city}, ${record.province}, ${record.postalcode}`}
      </div>
    ),
    width: 200,

    },
    {
      title: 'Phone',
      dataIndex: 'phone',
    },
    {
      title: 'Email',
      dataIndex: 'email',
    },
    {
      title: 'Actions',
      render: (_, record) => (
        <Space size="middle">
          <Button icon={<FaEye />} onClick={() => handleViewDetails(record)} />
          <Button icon={<FaEdit />} onClick={() => handleEditClick(record)} />
          {/* <Button icon={<FaTrash />} onClick={() => handleDelete(record)} danger /> */}
          <Button
            icon={<FaTrash />}
            onClick={() => openDeleteModal(record)}
            danger
          />
        </Space>
      ),
    },
  ];


  return (
    <div className="employee-table">
<div className="actions-container">

  <div className="view-employee-text">
    View Managers 
  </div>

  <Button 
    type="primary" 
    className="addbutton" 
    onClick={handleAddEmployee} 
    
  >
    + Add Employee
  </Button>
  </div>


      {/* Search Bar */}
      <div className="search-container">
        <Input
          type="text"
          placeholder="Search by Employee ID or Name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
      </div>

      {/* Employee Table */}
      <Spin spinning={loading} tip="Loading employees...">
      <div className="table-container">
      <Table
        columns={columns}
        dataSource={filteredEmployees}
        rowKey="empno"
        pagination={false}
        // scroll={{ x: "100%" }}
        scroll={isMobile ? { x: 'max-content'  } : {}}
        tableLayout="auto"
      />
      </div>
      </Spin>

      {/* Modal for View/Edit Employee */}
      {isModalOpen && selectedEmployee && (
        <Modal
          title={isEditMode ? 'Edit Employee Details' : 'Employee Details'}
          open={isModalOpen}
          onCancel={closeModal}
          footer={null}
          centered
          width={isEditMode ? 800 : 520}
        >
          {isEditMode ? (
            < EditEmployeeForm
              employee={selectedEmployee}
              onSubmit={handleEditEmployee}
              closeModal={closeModal}
            />
          ) : (
            <ViewEmployeeDetails
              employee={selectedEmployee}
              closeModal={closeModal}
            />
          )}
        </Modal>
      )}

<ConfirmationModal
        visible={isConfirmModalVisible}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        // message={`Are you sure you want to delete employee ${
        //   employeeToDelete ? employeeToDelete.firstname : ''
        // }?`}
        message={"Are you sure you want to Delete the Employee?"}
      />
    </div>
  );
};

export default EmployeeTable;



